import { getImpersonatedPersonId } from "../../../../common/local-storage/impersonated-person";
import { getPreferredLanguage } from "../../../../common/local-storage/language";
import { getOrganizationId } from "../../../../common/local-storage/selected-organization";
import { sanitizeFilename } from "../../../../common/utils/sanitizeUtils";
import { requireStringEnvVar } from "../../../../config";

/**
 * Streams a finalised protocol version PDF. The version's `downloadUrl` is a
 * BFF-relative path (not an S3 URL), so we resolve it against the BFF base and
 * attach the org/language/impersonation headers the secured endpoint expects.
 * Uses a direct fetch + blob rather than the generated query because RTK Query
 * cannot carry a binary stream.
 */
export const downloadProtocolVersion = async ({
  downloadUrl,
  fileName,
}: {
  downloadUrl: string;
  fileName: string;
}) => {
  const persistedOrganizationId = getOrganizationId();
  const impersonatedPersonId = getImpersonatedPersonId();
  const base = requireStringEnvVar("VITE_BFF_BASE_URL");
  const url = downloadUrl.startsWith("http") ? downloadUrl : `${base}${downloadUrl}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      ...(persistedOrganizationId ? { organizationId: persistedOrganizationId } : undefined),
      ...(impersonatedPersonId ? { personId: impersonatedPersonId } : undefined),
      "preferred-language": getPreferredLanguage(),
    },
  });

  if (!response.ok) throw new Error(`Failed to download protocol version (${response.status})`);

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = sanitizeFilename(`${fileName}.pdf`);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
};
